import { api } from "@/lib/api/client";

export interface QueryFilters {
  free_text: string | null;
  rfc_emisor: string[] | null;
  rfc_receptor: string[] | null;
  codigo_postal_prefix: string | null;
  fecha_desde: string | null;
  fecha_hasta: string | null;
  clave_prod_serv: string[] | null;
  moneda: string | null;
  min_valor_unitario: number | null;
  max_valor_unitario: number | null;
}

export interface DescriptionMatch {
  desc_hash_hex: string;
  text: string;
  most_common_clave_prod_serv: string | null;
  occurrences: number;
  rrf_score: number;
}

export interface ProductServiceMatch {
  clave_prod_serv: string;
  descripcion: string;
  palabras_similares: string | null;
  score: number;
  match_source: "exact_code" | "catalog_text" | "sqlite_overlap" | "fuzzy_catalog";
  concept_count: number;
}

export interface ConceptoSummary {
  concepto_id: number;
  uuid_cfdi: string;
  fecha_comprobante: string;
  rfc_emisor: string;
  nombre_emisor: string;
  codigo_postal_expedicion: string;
  clave_prod_serv: string;
  cantidad: number;
  clave_unidad: string;
  valor_unitario: number;
  moneda: string;
  descripcion: string;
}

export interface AggregateStats {
  matched_rows: number;
  unique_suppliers: number;
  avg_valor_unitario: number | null;
  min_valor_unitario: number | null;
  max_valor_unitario: number | null;
  earliest: string | null;
  latest: string | null;
}

export interface AgentTrace {
  step: string;
  started_at_ms: number;
  duration_ms: number;
  detail: Record<string, unknown> | null;
}

/**
 * Per-claim verdict produced by the symbolic verifier (`agent.verify`).
 * One badge per `VerificationClaim` is rendered next to the answer so the
 * analyst can see, at a glance, which numbers / RFCs / UNSPSCs / dates the
 * agent actually grounded in the corpus and which the LLM may have
 * invented. `verdict` is one of "verified" | "unsupported" | "out_of_scope".
 */
export interface VerificationClaim {
  kind: "rfc" | "unspsc" | "money" | "date" | "count";
  text: string;
  value: string;
  verdict: "verified" | "unsupported" | "out_of_scope";
  reason: string;
}

export interface SupplierRanking {
  rfc_emisor: string;
  nombre_emisor: string;
  transaction_count: number;
  total_spend: number;
  avg_unit_price: number;
  earliest: string | null;
  latest: string | null;
}

export interface ProductRanking {
  // `desc_hash_hex` is repurposed as the UNSPSC code identifier (8-digit SAT
  // code), since rows are now grouped by clave_prod_serv, not by desc_hash.
  // Kept under the legacy name so persisted responses remain readable.
  desc_hash_hex: string;
  text: string; // SAT official name (cfdi_producto_servicio.descripcion)
  most_common_clave_prod_serv: string | null;
  transaction_count: number;
  total_spend: number;
  unique_suppliers: number;
  // How many distinct CFDI free-text descriptions collapsed under this UNSPSC.
  unique_descriptions: number;
  min_unit_price: number;
  avg_unit_price: number;
  max_unit_price: number;
  earliest: string | null;
  latest: string | null;
}

export interface ProductPriceBySupplier {
  rfc_emisor: string;
  nombre_emisor: string;
  transaction_count: number;
  min_unit_price: number;
  avg_unit_price: number;
  max_unit_price: number;
  total_spend: number;
  earliest: string | null;
  latest: string | null;
  // Phase-1 unit normalization (KI-023, 2026-05-18). Populated when
  // the supplier's rows carry a SAT ClaveUnidad mappable to a base
  // unit (L, kg, m, m², h). UI shows the per-base column only when
  // normalizable_pct is high enough to be meaningful (>= 50 %).
  base_unit: string | null;
  min_price_per_base: number | null;
  avg_price_per_base: number | null;
  max_price_per_base: number | null;
  normalizable_pct: number | null;
}

export interface ProductPriceTrendPoint {
  month: string;
  transaction_count: number;
  min_unit_price: number;
  avg_unit_price: number;
  max_unit_price: number;
  unique_suppliers: number;
}

export interface AgentResponse {
  query: string;
  filters: QueryFilters;
  product_service_matches: ProductServiceMatch[];
  descriptions: DescriptionMatch[];
  concepts: ConceptoSummary[];
  stats: AggregateStats;
  supplier_rankings: SupplierRanking[];
  product_rankings: ProductRanking[];
  product_prices_by_supplier: ProductPriceBySupplier[];
  product_price_trend: ProductPriceTrendPoint[];
  answer_markdown: string;
  cited_concepto_ids: number[];
  cited_uuids: string[];
  verification: VerificationClaim[];
  trace: AgentTrace[];
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: number;
  latency_ms: number;
}

export interface ChatQueryResponse {
  response_id: string;
  conversation_id: string;
  agent: AgentResponse;
}

export function submitChatQuery(
  query: string,
  conversation_id?: string,
  source_id?: string | null,
) {
  return api.post<ChatQueryResponse>("/chat/queries", {
    query,
    ...(conversation_id ? { conversation_id } : {}),
    ...(source_id ? { source_id } : {}),
  });
}

export interface ConversationListItem {
  id: string;
  title: string;
  created_at: string;
  last_activity_at: string;
  message_count: number;
}

export interface ConversationTurnDTO {
  response_id: string;
  query: string;
  agent: AgentResponse;
  created_at: string;
}

export interface ConversationDetail {
  id: string;
  title: string;
  created_at: string;
  turns: ConversationTurnDTO[];
}

export function listConversations(limit = 50) {
  return api.get<ConversationListItem[]>(`/chat/conversations?limit=${limit}`);
}

export function getConversation(conversationId: string) {
  return api.get<ConversationDetail>(`/chat/conversations/${conversationId}`);
}
